import { useState, useEffect } from 'react'

import ExcelFileImport, {
    EXCEL_DATA_TYPES,
} from '../../GlobalComponents/ExcelFileImport/ExcelFileImport'
import stylesExcelFileImport from '../../GlobalComponents/ExcelFileImport/ExcelFileImport.module.scss'

import { AWCButton, BUTTON_TYPES } from '../../GlobalComponents/Inputs/Inputs'

import { LineItems } from './Components/LineItems'
import {
    NotificationManager,
    fqAlert,
} from '../../GlobalComponents/NotificationManager/NotificationManager'

import styles from './ImportCommissionOrders.module.scss'
import './ImportCommissionOrders.scss'

import _ from 'lodash'

import {
    fetchImportCommissionOrders,
    ImportCommissionOrdersReturn,
    CommissionOrderImport,
    OrderSavePayload,
    fetchSaveOrder,
} from '../../features/order/orderAPI'

import { LOADING_STATES } from '../../features/meta/metaSlice'
import { resetCustomerSearchFields } from '../../features/customer/customerSlice'
import VendorDropDownList from './Components/VendorDropDownList'
import { logEvent } from '../../GlobalComponents/GlobalFunctions'
import { useAppDispatch } from '../../app/hooks'

const PLACEHOLDER_ORDER_NUMBER = '9999999999'

export const ImportCommissionOrders = () => {
    const [importedData, setImportedData] = useState<any[]>()
    const [vendorReference, setVendorReference] = useState<string>('')

    const [importStatus, setImportStatus] = useState<LOADING_STATES>(LOADING_STATES.INIT)

    const [commissionOrders, setCommissionOrders] = useState<Array<ImportCommissionOrdersReturn>>(
        []
    )

    const [singleCorrectedCommissionsInput, setSingleCorrectedCommissionsInput] =
        useState<CommissionOrderImport>()

    const [chosenCommissionOrder, setChosenCommissionOrder] =
        useState<ImportCommissionOrdersReturn>()
    const [commissionsInput, setCommissionsInput] = useState<CommissionOrderImport>()

    const [unsavedOrderCount, setUnsavedOrderCount] = useState<number>(0)
    const [errorExists, setErrorExists] = useState<boolean>(false)

    const dispatch = useAppDispatch()
    const resetState = () => {
        setImportedData(undefined)
        setVendorReference('')
        setImportStatus(LOADING_STATES.INIT)
        setSingleCorrectedCommissionsInput(undefined)
        setChosenCommissionOrder(undefined)
        setCommissionsInput(undefined)
        setUnsavedOrderCount(0)
        setErrorExists(false)
        dispatch(resetCustomerSearchFields())
    }

    async function callAndSetImportCommissionOrders(input: CommissionOrderImport) {
        const commissionOrderPayload = await fetchImportCommissionOrders(input)

        let commissionOrdersWithNewValue = commissionOrderPayload

        // If correcting a single line item, replace previous version of line item with the new version in order list
        if (singleCorrectedCommissionsInput) {
            // Payload contains an order with a single updated line item
            const orderWithUpdatedLines = commissionOrderPayload[0]
            // Grab previous version of order
            const updatedVersionOfOrder = commissionOrders.filter(
                (c) => c.Order.InvoiceNumber === orderWithUpdatedLines.Order.InvoiceNumber
            )[0]
            let updatedLineItemList = orderWithUpdatedLines.Order.LineItems
            if (updatedLineItemList.length === 1) {
                // Grab updated line item
                const updatedLine = updatedLineItemList[0]
                // Replace previous version of line item with updated version
                updatedLineItemList = updatedVersionOfOrder.Order.LineItems.map((c) =>
                    c.Row === updatedLine.Row ? updatedLine : c
                )
            }
            updatedVersionOfOrder.Order.LineItems = updatedLineItemList
            updatedVersionOfOrder.Problems = orderWithUpdatedLines.Problems
            // Update objects to bind order information
            commissionOrdersWithNewValue = commissionOrders.map((c) =>
                c.FirstRow === updatedVersionOfOrder.FirstRow ? updatedVersionOfOrder : c
            )
            setChosenCommissionOrder(updatedVersionOfOrder)
        } else {
            if (commissionOrderPayload.length > 0) {
                // Choose first order for expediency
                let updatedVersionOfOrder = commissionOrderPayload[0]
                if (
                    // if correction is made on chosen order, update it in state to reflect correction
                    chosenCommissionOrder !== undefined
                ) {
                    updatedVersionOfOrder = commissionOrderPayload.filter(
                        (c) => c.Order.InvoiceNumber === chosenCommissionOrder.Order.InvoiceNumber
                    )[0]
                }
                setChosenCommissionOrder(updatedVersionOfOrder)
            }
        }

        setCommissionOrders(commissionOrdersWithNewValue)
        setSingleCorrectedCommissionsInput(undefined)
    }

    const trimInvoiceNumberForCommissionImport = (fpImportedInvoiceNumber: string) => {
        // Trim off prefix if invoice number is too long
        const INVOICE_NO_MAX_LEN = 10
        let newInvoiceNo = fpImportedInvoiceNumber

        if (newInvoiceNo.trim().length > INVOICE_NO_MAX_LEN) {
            if (newInvoiceNo.startsWith('SOI')) {
                newInvoiceNo = newInvoiceNo.substring(3)
            } else if (newInvoiceNo.startsWith('CN')) {
                newInvoiceNo = newInvoiceNo.substring(2)
            }
            // Trim off end if invoice number is still too long
            if (newInvoiceNo.trim().length > INVOICE_NO_MAX_LEN) {
                newInvoiceNo = newInvoiceNo.substring(0, INVOICE_NO_MAX_LEN)
            }
        }
        return newInvoiceNo
    }
    useEffect(() => {
        if (!commissionsInput) return

        commissionsInput.CommissionOrderItems = commissionsInput.CommissionOrderItems.map((c) => ({
            ...c,
            InvoiceNumber: { Value: trimInvoiceNumberForCommissionImport(c.InvoiceNumber.Value) },
        }))

        callAndSetImportCommissionOrders(commissionsInput)
    }, [commissionsInput])

    useEffect(() => {
        if (!singleCorrectedCommissionsInput) return

        callAndSetImportCommissionOrders(singleCorrectedCommissionsInput)
    }, [singleCorrectedCommissionsInput])

    useEffect(() => {
        if (commissionOrders.length === 0) resetState()

        setImportStatus(LOADING_STATES.DONE)

        let unsavedOrders = commissionOrders.filter(
            (co) => co.Order.OrderNumber === PLACEHOLDER_ORDER_NUMBER
        )
        setUnsavedOrderCount(unsavedOrders.length)

        let errorOrders = commissionOrders.filter(
            (co) =>
                co.Problems.length > 0 &&
                co.Problems.find((p: any) => p.Severity === 1) !== undefined
        )
        setErrorExists(errorOrders.length > 0)
    }, [commissionOrders])

    const onCreateOrdersClick = async () => {
        if (
            commissionOrders.find(
                (commOrder) =>
                    commOrder.Problems.length > 0 &&
                    commOrder.Problems.find((p: any) => p.Severity === 1) !== undefined
            )
        ) {
            fqAlert({
                type: 'error',
                title: 'Please make the corrections before adding to quote',
            })
        } else {
            setImportStatus(LOADING_STATES.LOADING)

            let totalSales = 0
            let totalCommission = 0
            let commissionOrdersWithNewValue = commissionOrders
            let commissionInputWithNewValue = commissionsInput
            // Save function - will be called in parallel for each commission order
            const saveOrder = async (commOrder: ImportCommissionOrdersReturn) => {
                const savePayload: OrderSavePayload =
                    convertImportCommissionOrdersReturnToOrderSavePayload(commOrder)

                const respSave = await fetchSaveOrder(savePayload)
                const updatedCommOrder = commissionOrders.filter(
                    (c) => c.FirstRow === commOrder.FirstRow
                )[0]

                if (respSave.ErrorMessage && respSave.ErrorMessage !== '') {
                    // If an error occurred, update list and show error on screen
                    updatedCommOrder.Problems.push({
                        Value: '',
                        Name: '',
                        FieldName: '',
                        Message: respSave.ErrorMessage,
                        Severity: 1,
                    })
                } else {
                    // Update commissionOrders with new OrderNumber to show user
                    updatedCommOrder.Order.OrderNumber = respSave.OrderHeaderView[0].OrderNumber

                    commissionOrdersWithNewValue = commissionOrders.map((c) =>
                        c.FirstRow === commOrder.FirstRow ? updatedCommOrder : c
                    )

                    // Update commissionsInput with new OrderNumber so the record can't be changed anymore
                    commissionInputWithNewValue!.CommissionOrderItems =
                        commissionsInput!.CommissionOrderItems.map((s) =>
                            s.InvoiceNumber.Value === commOrder.Order.InvoiceNumber
                                ? {
                                      ...s,
                                      OrderNumber: {
                                          Value: respSave.OrderHeaderView[0].OrderNumber,
                                      },
                                  }
                                : s
                        )

                    // Sum up sales of orders with that saved successfully)
                    totalSales += respSave.OrderHeaderView[0].OrderValue.SubTotal

                    // Sum up commission of orders with that saved successfully)
                    totalCommission += respSave.OrderHeaderView[0].OrderValue.CommissionSubTotal
                }
            }

            // Save all commission orders in parallel
            try {
                // Map the commission orders array to an array of promises
                const savePromises = commissionOrdersWithNewValue.map((commOrder) =>
                    saveOrder(commOrder)
                )

                // Wait for all promises to resolve
                await Promise.all(savePromises)

                // Process the results
                setCommissionOrders([...commissionOrdersWithNewValue])
                setChosenCommissionOrder(
                    commissionOrdersWithNewValue.length === 1
                        ? commissionOrdersWithNewValue[0]
                        : undefined
                )
                setCommissionsInput(commissionInputWithNewValue)

                dispatch(resetCustomerSearchFields())

                const successCount = commissionOrdersWithNewValue.filter(
                    (co) => co.Order.OrderNumber !== PLACEHOLDER_ORDER_NUMBER
                ).length
                const skippedCount = commissionOrdersWithNewValue.length - successCount
                fqAlert({
                    type: 'buttons',
                    title: 'Import Complete',
                    message: (
                        <>
                            <div>
                                {successCount} order{successCount === 1 ? '' : 's'} saved
                                successfully:
                            </div>
                            <table style={{ textAlign: 'right' }}>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Total sales:</td>
                                    <td>
                                        {totalSales.toLocaleString('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                        })}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ fontWeight: 'bold' }}>Total commission:</td>
                                    <td>
                                        {totalCommission.toLocaleString('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                        })}
                                    </td>
                                </tr>
                            </table>
                            {skippedCount > 0 && (
                                <div style={{ marginTop: '15px' }}>
                                    {skippedCount} order{skippedCount === 1 ? ' ' : 's '}
                                    skipped. Please review manually.
                                </div>
                            )}
                        </>
                    ),
                    buttons: [{ onClick: () => null, type: BUTTON_TYPES.BLUE, text: 'OK' }],
                })
                logEvent({
                    eventName: 'Commission Orders Imported',
                    string2: commissionInputWithNewValue?.VendorReference,
                    decimal1: successCount,
                    decimal2: skippedCount,
                })
            } catch (error) {
                // Handle errors if any of the promises reject
                console.error('Error occurred:', error)
            }
        }
    }

    const allCommissionOrdersSavedSuccessfully =
        commissionOrders.find((c) => c.Order.OrderNumber === PLACEHOLDER_ORDER_NUMBER) === undefined

    return (
        <div className={styles.dropZone} data-testid='importCommissionOrdersDiv'>
            <div className={`${styles.importCommissionOrders} importCommissionOrders`}>
                {importStatus !== LOADING_STATES.LOADING && commissionOrders?.length === 0 ? (
                    <>
                        {importedData && (
                            <div
                                className={stylesExcelFileImport.importMapping}
                                style={{ marginBottom: '10px' }}
                            >
                                <div className={stylesExcelFileImport.importMappingLists}>
                                    <div
                                        className={`${stylesExcelFileImport.mappingListItem} ${stylesExcelFileImport.requiredAfter}`}
                                        key={'vendRefListItem'}
                                    >
                                        <div className={stylesExcelFileImport.label}>
                                            Vendor Reference
                                        </div>
                                        <div>
                                            <VendorDropDownList
                                                selectedValue={vendorReference}
                                                onSelectedValueChanged={(selectedVendor) =>
                                                    setVendorReference(selectedVendor)
                                                }
                                                style={{ backgroundColor: 'white', width: '275px' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <ExcelFileImport
                            returnHeaderRowInResults={true}
                            onFileDropped={(importJsonData: any[]) => {
                                setImportedData(importJsonData)
                            }}
                            onFileImported={(importedData: any[]) => {
                                if (vendorReference === '') {
                                    fqAlert({
                                        type: 'error',
                                        title: 'Required Field Missing',
                                        message: 'Please select a Vendor Reference.',
                                    })
                                    return false
                                }

                                if (importedData.length > 0) {
                                    if (
                                        importedData[0].CustomerAccountCode === undefined &&
                                        importedData[0].ShipToName === undefined
                                    ) {
                                        fqAlert({
                                            type: 'error',
                                            title: 'Invalid Field Imported',
                                            message:
                                                'Customer Account Code or Customer Name must be present!',
                                        })
                                        return false
                                    }

                                    if (
                                        importedData[0].UnitPrice !== undefined &&
                                        importedData[0].ExtPrice !== undefined
                                    ) {
                                        fqAlert({
                                            type: 'error',
                                            title: 'Invalid Field Imported',
                                            message:
                                                'Only Unit Price or Extended Price can be imported. Not both.',
                                        })
                                        return false
                                    }
                                }

                                // Add customer account code field for user to set if needed
                                const importedCommissionData = {
                                    VendorReference: vendorReference,
                                    CommissionOrderItems: importedData
                                        .map((d) => ({
                                            ...d,
                                            OrderNumber: { Value: PLACEHOLDER_ORDER_NUMBER },
                                        }))
                                        .filter((co) => co.InvoiceNumber.Value.trim() !== ''),
                                }

                                setImportStatus(LOADING_STATES.LOADING)
                                setCommissionsInput(importedCommissionData)
                            }}
                            columnList={[
                                {
                                    fieldName: 'CustomerAccountCode',
                                    fieldLabel: 'Customer Account Code',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 35,
                                },
                                {
                                    fieldName: 'ShipToName',
                                    fieldLabel: 'Customer Name',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 35,
                                },
                                {
                                    fieldName: 'InvoiceNumber',
                                    fieldLabel: 'Invoice Number',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 10,
                                    required: true,
                                },
                                {
                                    fieldName: 'CustomerPONumber',
                                    fieldLabel: 'Customer Order Number',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 30,
                                    required: true,
                                },
                                {
                                    fieldName: 'ShipToAddress1',
                                    fieldLabel: 'Ship To Address',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 35,
                                },
                                {
                                    fieldName: 'PartNumber',
                                    fieldLabel: 'Part Number',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 28,
                                    required: true,
                                },
                                {
                                    fieldName: 'ShipToCity',
                                    fieldLabel: 'Ship To City',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 35,
                                    required: true,
                                },
                                {
                                    fieldName: 'OrderQuantity',
                                    fieldLabel: 'Quantity',
                                    dataType: EXCEL_DATA_TYPES.Decimal,
                                    required: true,
                                },
                                {
                                    fieldName: 'ShipToState',
                                    fieldLabel: 'Ship To State',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 5,
                                },
                                {
                                    fieldName: 'UnitPrice',
                                    fieldLabel: 'Unit Price',
                                    dataType: EXCEL_DATA_TYPES.Decimal,
                                },
                                {
                                    fieldName: 'ShipToZipcode',
                                    fieldLabel: 'Ship To Zip',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 10,
                                },
                                {
                                    fieldName: 'ExtPrice',
                                    fieldLabel: 'Extended Price',
                                    dataType: EXCEL_DATA_TYPES.Decimal,
                                },

                                {
                                    fieldName: 'CommissionProductCode',
                                    fieldLabel: 'Commission Product Code',
                                    dataType: EXCEL_DATA_TYPES.String,
                                    maxDataLength: 2,
                                    required: true,
                                },
                                {
                                    fieldName: 'Description',
                                    fieldLabel: 'Description',
                                    dataType: EXCEL_DATA_TYPES.String,
                                },
                                {
                                    fieldName: 'CommissionPercentage',
                                    fieldLabel: 'Commission Percentage',
                                    dataType: EXCEL_DATA_TYPES.Decimal,
                                },
                                {
                                    fieldName: 'InvoiceDate',
                                    fieldLabel: 'Ship Date',
                                    dataType: EXCEL_DATA_TYPES.Date,
                                },
                            ]}
                        />
                    </>
                ) : (
                    <>
                        {(() => {
                            switch (importStatus) {
                                case LOADING_STATES.LOADING:
                                    return (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                width: '100%',
                                                height: 'calc(100% - 2rem)',
                                                zIndex: 10,
                                            }}
                                        >
                                            <div
                                                className={'centered'}
                                                style={{ height: 'calc(100% - 2rem)' }}
                                            >
                                                <div
                                                    className={'k-i-loading'}
                                                    style={{ fontSize: 100, height: '100px' }}
                                                />
                                            </div>
                                        </div>
                                    )
                                case LOADING_STATES.DONE:
                                    return (
                                        commissionsInput && (
                                            <LineItems
                                                commissionsInput={commissionsInput}
                                                UpdateCommissionsInput={(
                                                    newVal?: CommissionOrderImport
                                                ) => setCommissionsInput(newVal)}
                                                commissionOrders={commissionOrders}
                                                chosenCommissionOrder={chosenCommissionOrder}
                                                OnChosenCommissionOrderChange={(
                                                    dataItem?: ImportCommissionOrdersReturn
                                                ) => setChosenCommissionOrder(dataItem)}
                                                importStatus={importStatus}
                                                OnCorrectionMade={
                                                    setSingleCorrectedCommissionsInput
                                                }
                                            />
                                        )
                                    )
                                default:
                                    return null
                            }
                        })()}
                        <div className={styles.importCommissionOrdersButtonContainer}>
                            {importStatus === LOADING_STATES.DONE && (
                                <>
                                    <AWCButton
                                        type={'secondary'}
                                        onClick={() => {
                                            resetState()
                                            setCommissionOrders([])
                                        }}
                                        style={{ marginRight: '5px' }}
                                    >
                                        {allCommissionOrdersSavedSuccessfully
                                            ? 'Start Over'
                                            : 'Cancel'}
                                    </AWCButton>
                                    {!allCommissionOrdersSavedSuccessfully && (
                                        <AWCButton
                                            type={errorExists ? 'disabled' : 'primary'}
                                            onClick={onCreateOrdersClick}
                                        >
                                            {`Create ${
                                                unsavedOrderCount === 1
                                                    ? ''
                                                    : `${unsavedOrderCount} `
                                            }Order${unsavedOrderCount === 1 ? '' : 's'}`}
                                        </AWCButton>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
            {/* Keep at highest common level */}
            <NotificationManager key={'notificationManager'} />
        </div>
    )
}
const convertImportCommissionOrdersReturnToOrderSavePayload = (
    commOrder: ImportCommissionOrdersReturn
): OrderSavePayload => {
    let oReturn = {
        OrderHeaderView: [
            {
                OrderNumber: commOrder.Order.OrderNumber,
                Location: commOrder.Order.Location,
                CustomerAccountCode: commOrder.Order.CustomerAccountCode,
                PreviousCustomer: commOrder.Order.PreviousCustomer,
                Restrictions: commOrder.Order.Restrictions,
                Username: commOrder.Order.Username,
                Terms: commOrder.Order.Terms,
                NeedDate: commOrder.Order.NeedDate?.toString(),
                UnusualDemand: commOrder.Order.UnusualDemand,
                CustomerOrderNumber: commOrder.Order.CustomerOrderNumber,
                TaxAuthorityCode: commOrder.Order.TaxAuthorityCode,
                ShipmentMethod: commOrder.Order.ShipmentMethod,
                ShipmentMethodDescription: commOrder.Order.ShipmentMethodDescription,
                ShipmentTrackingNumber: commOrder.Order.ShipmentTrackingNumber,
                OrderDate: commOrder.Order.OrderDate?.toString(),
                HighPriority: commOrder.Order.HighPriority,
                BillToName: commOrder.Order.BillToName,
                BillToAddress1: commOrder.Order.BillToAddress1,
                BillToAddress2: commOrder.Order.BillToAddress2,
                BillToAddress3: commOrder.Order.BillToAddress3,
                BillToCountry: commOrder.Order.BillToCountry,
                BillToCity: commOrder.Order.BillToCity,
                BillToState: commOrder.Order.BillToState,
                BillToZipcode: commOrder.Order.BillToZipcode,
                ShipToName: commOrder.Order.ShipToName,
                ShipToAddress1: commOrder.Order.ShipToAddress1,
                ShipToAddress2: commOrder.Order.ShipToAddress2,
                ShipToAddress3: commOrder.Order.ShipToAddress3,
                ShipToCountry: commOrder.Order.ShipToCountry,
                ShipToCity: commOrder.Order.ShipToCity,
                ShipToState: commOrder.Order.ShipToState,
                ShipToZipcode: commOrder.Order.ShipToZipcode,
                SpecialInstructions: commOrder.Order.SpecialInstructions,
                InternalInstructions: commOrder.Order.InternalInstructions,
                CancelFlag: commOrder.Order.CancelFlag,
                ReservedFlag: commOrder.Order.ReservedFlag,
                ClosedStatus: commOrder.Order.ClosedStatus,
                SalesPerson1: commOrder.Order.SalesPerson1,
                SalesPerson2: commOrder.Order.SalesPerson2,
                Percentage1: commOrder.Order.Percentage1,
                IsQuote: commOrder.Order.IsQuote,
                Type: commOrder.Order.Type,
                QuoteProbability: commOrder.Order.QuoteProbability,
                Attention: commOrder.Order.Attention,
                QuoteExpirationDate: commOrder.Order.QuoteExpirationDate?.toString(),
                QuoteOpenNotes: commOrder.Order.QuoteOpenNotes,
                QuoteCloseNotes: commOrder.Order.QuoteCloseNotes,
                VendorID: commOrder.Order.VendorID,
                CustomerNotes: commOrder.Order.CustomerNotes,
                Contact: commOrder.Order.Contact,
                CardNumber: commOrder.Order.CardNumber,
                CardExpirationDate: commOrder.Order.CardExpirationDate?.toString(),
                CardOwner: commOrder.Order.CardOwner,
                CardSecurityCode: commOrder.Order.CardSecurityCode,
                CardBillingAddress1: commOrder.Order.CardBillingAddress1,
                CardBillingAddress2: commOrder.Order.CardBillingAddress2,
                CardBillingCountry: commOrder.Order.CardBillingCountry,
                CardBillingCity: commOrder.Order.CardBillingCity,
                CardBillingState: commOrder.Order.CardBillingState,
                CardBillingZipcode: commOrder.Order.CardBillingZipcode,
                CardReceiptEmail: commOrder.Order.CardReceiptEmail,
                AMEXExtNumber: commOrder.Order.AMEXExtNumber,
                OrderEntryType: commOrder.Order.OrderEntryType,
                ReservedByUser: commOrder.Order.ReservedByUser,
                ReservedDate: new Date(commOrder.Order['InvoiceDate'])?.toLocaleDateString(),
                // ReservedDate: commOrder.Order.ReservedDate?.toString(),
                WasUpdated: commOrder.Order.WasUpdated,
                QuotedDelivery: commOrder.Order.QuotedDelivery,
                LocationName: commOrder.Order.LocationName,
                IsStockTransfer: commOrder.Order.IsStockTransfer,
                IsFactoryReturn: commOrder.Order.IsFactoryReturn,
                SendPOAckViaEDI: commOrder.Order.SendPOAckViaEDI,
                POAckPartnerID: commOrder.Order.POAckPartnerID,
                POAckSendCode: commOrder.Order.POAckSendCode,
                POAckQualifier: commOrder.Order.POAckQualifier,
                ReminderSent: commOrder.Order.ReminderSent?.toString(),
                UserEmail: commOrder.Order.UserEmail,
                NotesAfterClosed: commOrder.Order.NotesAfterClosed,
                WarehouseNotes: commOrder.Order.WarehouseNotes,
                SendShipNoticeTo: commOrder.Order.SendShipNoticeTo,
                QuoteReasonLost: commOrder.Order.QuoteReasonLost,
                QuoteReasonLostDescription: commOrder.Order.QuoteReasonLostDescription,
                QuoteInsideFollowUp: commOrder.Order.QuoteInsideFollowUp,
                ReturnedInvoiceNumber: commOrder.Order.ReturnedInvoiceNumber,
                ReturnedOrderNumber: commOrder.Order.ReturnedOrderNumber,
                OpportunityNumber: commOrder.Order.OpportunityNumber,
                Revision: commOrder.Order.Revision,
                CreditReason: commOrder.Order.CreditReason,
                IsMRPPlannedOrder: commOrder.Order.IsMRPPlannedOrder,
                OriginalInvoiceNumber: commOrder.Order['InvoiceNumber'],
                // OriginalInvoiceNumber: commOrder.Order.OriginalInvoiceNumber,
                RFQNumber: commOrder.Order.RFQNumber,
                ExpectedOnDockDate: commOrder.Order.ExpectedOnDockDate?.toString(),
                OriginalOnDockDate: commOrder.Order.OriginalOnDockDate?.toString(),
                CartName: commOrder.Order.CartName,
                AvailabilityMode: commOrder.Order.AvailabilityMode,
                CustomerOrderAmount: commOrder.Order.CustomerOrderAmount,
                CustomerRequirements: commOrder.Order.CustomerRequirements,
                AllocationWarehouse: commOrder.Order.AllocationWarehouse,
                FactoryReturnReason: commOrder.Order.FactoryReturnReason,
                OrderControlFlag: commOrder.Order.OrderControlFlag,
                SendInvoicesViaEDI: commOrder.Order.SendInvoicesViaEDI,
                isFreeIssue: commOrder.Order.isFreeIssue,
                OrderDetailView: commOrder.Order.LineItems.map((li) => {
                    return {
                        OrderNumber: li.LineItem.OrderNumber,
                        LineItemNumber: li.LineItem.LineItemNumber,
                        CancelFlag: li.LineItem.CancelFlag,
                        NeedDate: li.LineItem.NeedDate?.toString(),
                        UnusualDemand: li.LineItem.UnusualDemand,
                        TransitTime: li.LineItem.TransitTime,
                        OrderEntryDate: li.LineItem.OrderEntryDate?.toString(),
                        HighPriority: li.LineItem.HighPriority,
                        PartKey: li.LineItem.PartKey,
                        PartNumber: li.LineItem.PartNumber,
                        Description: li.LineItem.Description,
                        SpecialInstructions: li.LineItem.SpecialInstructions,
                        InternalInstructions: li.LineItem.InternalInstructions,
                        OrderQuantity: li.LineItem.OrderQuantity,
                        ShipQuantity: li.LineItem.ShipQuantity,
                        UnitPrice: li.LineItem.UnitPrice,
                        IsAssembly: li.LineItem.IsAssembly,
                        AssemblyType: li.LineItem.AssemblyType,
                        QuantityReadyForShip: li.LineItem.QuantityReadyForShip,
                        QueueDate: li.LineItem.QueueDate?.toString(),
                        CommissionPercentage: li.LineItem.CommissionPercentage,
                        CommissionProductCode: li.LineItem.CommissionProductCode,
                        QuoteNote: li.LineItem.QuoteNote,
                        ExpectedOnDockDate: li.LineItem.ExpectedOnDockDate?.toString(),
                        OriginalOnDockDate: li.LineItem.OriginalOnDockDate?.toString(),
                        QuotedExpeditedDeliveryDate:
                            li.LineItem.QuotedExpeditedDeliveryDate?.toString(),
                        LastNotifiedOnDockDate: li.LineItem.LastNotifiedOnDockDate?.toString(),
                        DateOfLastOnDockNotification:
                            li.LineItem.DateOfLastOnDockNotification?.toString(),
                        AllocatedDate: li.LineItem.AllocatedDate?.toString(),
                        ShippedDate: li.LineItem.ShippedDate?.toString(),
                        CustomerLineItemNumber: li.LineItem.CustomerLineItemNumber,
                        CustomerSpecificNotes: li.LineItem.CustomerSpecificNotes,
                        WorkOrderNumber: li.LineItem.WorkOrderNumber,
                        SymbolNumber: li.LineItem.SymbolNumber,
                        WasUpdated: li.LineItem.WasUpdated,
                        QuotedDelivery: li.LineItem.QuotedDelivery,
                        QuotedDeliveryUOM: li.LineItem.QuotedDeliveryUOM,
                        QuotedDeliveryOverride: li.LineItem.QuotedDeliveryOverride,
                        LockInPONumber: li.LineItem.LockInPONumber,
                        LockInPOLineItemNumber: li.LineItem.LockInPOLineItemNumber,
                        LockInOrderFromCode: li.LineItem.LockInOrderFromCode,
                        LockInOrderFromName: li.LineItem.LockInOrderFromName,
                        LockInIsDropship: li.LineItem.LockInIsDropship,
                        LockInOrdQty: li.LineItem.LockInOrdQty,
                        LockInShipQty: li.LineItem.LockInShipQty,
                        LockInExpectedShipDate: li.LineItem.LockInExpectedShipDate?.toString(),
                        LockInRequestedShipDateOverride:
                            li.LineItem.LockInRequestedShipDateOverride?.toString(),
                        LockInOnDockDate: li.LineItem.LockInOnDockDate?.toString(),
                        LockInTransitTime: li.LineItem.LockInTransitTime,
                        LockInCost: li.LineItem.LockInCost,
                        LockInFactorySpecifications: li.LineItem.LockInFactorySpecifications,
                        SORListPrice: li.LineItem.SORListPrice,
                        SORDiscount: li.LineItem.SORDiscount,
                        SORTagInfo: li.LineItem.SORTagInfo,
                        SORSetPoint: li.LineItem.SORSetPoint,
                        SORPlusMinus: li.LineItem.SORPlusMinus,
                        SORCustNumber: li.LineItem.SORCustNumber,
                        SORRepNo: li.LineItem.SORRepNo,
                        SORCustContact: li.LineItem.SORCustContact,
                        SORTelephone: li.LineItem.SORTelephone,
                        DesiredWarehouseQuantity: li.LineItem.DesiredWarehouseQuantity,
                        SalesProductCode: li.LineItem.SalesProductCode,
                        OperationsProductCode: li.LineItem.OperationsProductCode,
                        LeadTime: li.LineItem.LeadTime,
                        PartDescription: li.LineItem.PartDescription,
                        ListPrice: li.LineItem.ListPrice,
                        Cost: li.LineItem.Cost,
                        LockInVend_SO: li.LineItem.LockInVend_SO,
                        LockInVend_Line_Item: li.LineItem.LockInVend_Line_Item,
                        LockInExp_Status: li.LineItem.LockInExp_Status,
                        PriceOverrideDiff: li.LineItem.PriceOverrideDiff,
                        VendorContractNo: li.LineItem.VendorContractNo,
                        ReleaseQuantity: li.LineItem.ReleaseQuantity,
                        NoPartials: li.LineItem.NoPartials,
                        MarketingCode: li.LineItem.MarketingCode,
                        OnTimeDelivery: li.LineItem.OnTimeDelivery,
                        LastQuantityAcknowledged: li.LineItem.LastQuantityAcknowledged,
                        LastUnitPriceAcknowledged: li.LineItem.LastUnitPriceAcknowledged,
                        CustomerStatus: li.LineItem.CustomerStatus,
                        Available: li.LineItem.Available,
                        BalanceShipDate: li.LineItem.BalanceShipDate?.toString(),
                        OrderDetailId: li.LineItem.OrderDetailId,
                        TechnicianInstructions: li.LineItem.TechnicianInstructions,
                        ServiceType: li.LineItem.ServiceType,
                        LockInTariff: li.LineItem.LockInTariff,
                        RebateBasis: li.LineItem.RebateBasis,
                        SalesPerson1: li.LineItem.SalesPerson1,
                        SalesPerson2: li.LineItem.SalesPerson2,
                        SalesPercentage: li.LineItem.SalesPercentage,
                        Rebate: li.LineItem.Rebate,
                        PrintAssemblyComponentsOnAcknowledgement:
                            li.LineItem.PrintAssemblyComponentsOnAcknowledgement,
                        PrintAssemblyComponentsOnQuote: li.LineItem.PrintAssemblyComponentsOnQuote,
                        PrintAssemblyComponentsOnDeliveryTicket:
                            li.LineItem.PrintAssemblyComponentsOnDeliveryTicket,
                        PrintAssemblyComponentsOnOrder: li.LineItem.PrintAssemblyComponentsOnOrder,
                        PrintAssemblyComponentsOnInvoice:
                            li.LineItem.PrintAssemblyComponentsOnInvoice,
                        OrderAssemblyDetailView: [],
                        rowId: '',
                        temp: false,
                        includeDataSheets: false,
                    }
                }),
                OrderFilenames: [],
                ProgressBillingScheduleView: [],
                EmailCC: '',
                WebSubmitOrderURL: '',
                OrderValue: {
                    Cost: 0,
                    Tariff: 0,
                    SubTotal: 0,
                    CommissionSubTotal: 0,
                    FreightEstimate: 0,
                    FreightErrorMessage: '',
                    Total: 0,
                    TaxAmt: 0,
                    TaxPct: 0,
                    DesignGuideSubtotal: 0,
                    RebateTotal: 0,
                    ReplacementCostUsed: false,
                    ZeroCostLinesFound: false,
                    AvgTierCostUsed: false,
                    LastCostUsed: false,
                    VendorAlloc: {},
                    SalesByInventoryType: {},
                    CostByInventoryType: {},
                    InvoicedByInventoryType: {},
                },
            },
        ],
        ErrorMessage: '',
        IncludeMiscInfo: true,
        ConfirmCommissions: true,
    }

    return oReturn
}
